import { Suspense, lazy } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { TracedRoutes } from '@comeon/error-reporter';
import * as Paths from '~/components/router/constants.js';
import ProductNavbar from '~/components/header/product-navbar/product-navbar-async.js';
import { ROUTE_WE_SPIN_GAME_VIEW } from '~/components/we-spin/new-flow/constants/we-spin-route-constants.js';
import { useSelector } from 'react-redux';
import HeaderSelector from '~/components/header/header-selector.js';
import { selectGlobalNavigationEnabled } from '~/components/brand/ducks/selectors.js';
import { isSmallSelector, Smaller } from '~/components/layout/mediaquery';
import CasinoTabs from '~/components/casino/casino-tabs.js';
import NullLoader from '~/components/null-loader.js';
import { isIOS } from '~/utils/layout-utils.js';
import ScrollAdapt from '~/components/misc/scroll-adapt.js';
import styled from 'styled-components';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import { selectHasRegulationsHeader } from '~/components/header/regulations-header';
import { ProductNavbarFilters as ProductFilters } from '~/components/header/filters';
import { getZindexByType } from '~/utils/style-utils.ts';
import { selectPendingBets } from '~/components/highlight-games/ducks/highlight-games-duck';
import { isNativeBottomNavigationEnabled } from '~/components/app-bridge/app-properties.js';

const BottomNavigation = lazy(() =>
	import(
		/* webpackChunkName: "global-navigation" */ '~/components/global-navigation/bottom-navigation/bottom-navigation'
	)
);

/* FIXME: do we need this ??
RoutesSubheader is mounted when hasSubheader is true, doesnt that take care of this?
Seems like if we add this, Product navbar is not mounted */
const SUB_HEADER_PATHS = [
	Paths.ROUTE_CASINO,
	Paths.ROUTE_LIVE_CASINO,
	Paths.ROUTE_SPORTSBOOK,
	Paths.ROUTE_LIVEBETTING,
	Paths.ROUTE_SHOP,
	Paths.ROUTE_PROMOTIONS,
	Paths.ROUTE_ABOUT,
	Paths.ROUTE_TERMSANDCONDITIONS,
	Paths.ROUTE_PRIVACYPOLICY,
	Paths.ROUTE_RESPONSIBLEGAMING,
	Paths.ROUTE_BONUS,
	Paths.ROUTE_TVBET,
	Paths.ROUTE_USERFEEDBACK,
	Paths.ROUTE_WE_SPIN,
	Paths.ROUTE_FREEBET,
	Paths.ROUTE_LEGAL_BETTING,
	Paths.ROUTE_BETTING_TIPS,
	Paths.ROUTE_TAX,
	Paths.ROUTE_THUNDERBITE,
	Paths.ROUTE_SPLASH_TECH,
	Paths.ROUTE_VERIFICATION_POLAND,
	Paths.ROUTE_PAYMENTS_POLAND,
	Paths.ROUTE_VIRTUALS,
	Paths.ROUTE_FREE_TO_PLAY,
	Paths.ROUTE_FREE_TO_PLAY_CUSTOM
];

const ROUTES_WITH_NO_SUBHEADER = [Paths.ROUTE_CASINO_GAME, Paths.ROUTE_LIVE_CASINO_GAME, ROUTE_WE_SPIN_GAME_VIEW];

const StickyFooter = styled(ScrollAdapt)`
	z-index: ${getZindexByType('small-product-nav')};
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
`;

const StyledSmallProductFilters = styled(ProductFilters)`
	padding: 0;
`;

const UNAUTH_BUTTON_WRAPPER_HEIGHT = '75px';

const variants = {
	visible: { y: 0 },
	hidden: custom => ({
		y: custom ? '100%' : `calc(100% + ${UNAUTH_BUTTON_WRAPPER_HEIGHT})`
	})
};

const transition = { ease: [0.1, 0.25, 0.3, 1], duration: 0.3 };

const SmallProductFilters = ({ forceVisible, safariToolbarIsHidden, authenticated, ...rest }) => (
	<StickyFooter
		disableInlineStyles
		className={forceVisible ? 'product-filter--visible' : ''}
		forceVisible={forceVisible}
		variants={variants}
		transition={transition}
		custom={authenticated}
	>
		<StyledSmallProductFilters
			className="product-filter__small"
			isIOS={isIOS()}
			safariToolbarIsHidden={safariToolbarIsHidden}
			{...rest}
			dataAt="product-navbar-mobile"
		/>
	</StickyFooter>
);

const CasinoTabsWithLocation = ({ hasRegulationsHeader, authenticated, safariToolbarIsHidden }) => {
	const location = useLocation();
	if (location.pathname === Paths.ROUTE_CASINO_SEARCH) {
		return null;
	}

	return (
		<SmallProductFilters
			hasRegulationsHeader={hasRegulationsHeader}
			authenticated={authenticated}
			safariToolbarIsHidden={safariToolbarIsHidden}
		>
			<CasinoTabs />
		</SmallProductFilters>
	);
};

const RoutesSubheader = () => {
	const smaller = useSelector(isSmallSelector);
	const authenticated = useSelector(authenticationSelector);
	const hasRegulationsHeader = useSelector(selectHasRegulationsHeader);
	const bottomReached = useSelector(state => state.scroll.bottomReached);
	const safariToolbarIsHidden = useSelector(state => state.scroll.safariToolbarIsHidden);
	const isHGBetslipWithNoSelections = useSelector(selectPendingBets);
	const isGlobalNavigationEnabled = useSelector(selectGlobalNavigationEnabled);
	const hasNativeBottomNavigation = isNativeBottomNavigationEnabled();

	return (
		<>
			<TracedRoutes>
				{/* Common sub header  */}
				{SUB_HEADER_PATHS.map(path => (
					<Route
						path={`${path}/*`}
						key={path}
						element={
							<Suspense fallback={<NullLoader />}>
								<ProductNavbar />
							</Suspense>
						}
					/>
				))}
				{ROUTES_WITH_NO_SUBHEADER.map(path => (
					<Route path={`${path}/*`} key={path} />
				))}
				<Route path="*" element={<NullLoader />} />
			</TracedRoutes>
			<TracedRoutes>
				{/* Product specific sub header and navigation elements  */}
				<Route
					path={Paths.ROUTE_HIGHLIGHT_GAMES}
					element={
						<Smaller>
							<StickyFooter
								disableInlineStyles
								className={bottomReached ? 'product-filter--visible' : ''}
							>
								<HeaderSelector
									isHGBetslipWithNoSelections={isHGBetslipWithNoSelections.length === 0}
								/>
							</StickyFooter>
						</Smaller>
					}
				/>

				{smaller &&
					!isGlobalNavigationEnabled &&
					[Paths.ROUTE_CASINO, Paths.ROUTE_LIVE_CASINO].map(path => (
						<Route
							path={`${path}/*`}
							key={path}
							element={
								<CasinoTabsWithLocation
									hasRegulationsHeader={hasRegulationsHeader}
									authenticated={authenticated}
									safariToolbarIsHidden={safariToolbarIsHidden}
								/>
							}
						/>
					))}
				{smaller &&
					isGlobalNavigationEnabled &&
					!hasNativeBottomNavigation &&
					[Paths.ROUTE_CASINO, Paths.ROUTE_LIVE_CASINO, Paths.ROUTE_SPORTSBOOK].map(path => (
						<Route
							path={`${path}/*`}
							key={path}
							element={
								<Suspense fallback={<NullLoader />}>
									<BottomNavigation />
								</Suspense>
							}
						/>
					))}
				{smaller &&
					[Paths.ROUTE_SHOP, Paths.ROUTE_PROMOTIONS, Paths.ROUTE_WE_SPIN].map(path => (
						<Route
							path={`${path}/*`}
							key={path}
							element={
								<StickyFooter
									disableInlineStyles
									className={bottomReached ? 'product-filter--visible' : ''}
								>
									<HeaderSelector />
								</StickyFooter>
							}
						/>
					))}
				<Route path="*" element={<NullLoader />} />
			</TracedRoutes>
			<TracedRoutes>
				{/* Add CTA on top of bottom-nav  */}
				{smaller &&
					isGlobalNavigationEnabled &&
					!hasNativeBottomNavigation &&
					[Paths.ROUTE_CASINO, Paths.ROUTE_LIVE_CASINO].map(path => (
						<Route path={`${path}/*`} key={path} element={<HeaderSelector hasBottomOffset />} />
					))}
			</TracedRoutes>
		</>
	);
};

export default RoutesSubheader;
